import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
// import ProductListFiltersAccordionSection, { // Store Custom Code
//    ProductListFilterAccordionSectionStyles, // Store Custom Code
// } from "@insite/content-library/Widgets/ProductList/ProductListFilterAccordionSection"; // Store Custom Code
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

/* Store Custom Code - Start */
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import ProductListFiltersAccordionSection, {
    ProductListFilterAccordionSectionStyles,
    productListFilterAccordionSectionStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListFilterAccordionSection";
import { StyleProps as CheckboxStyleProps } from "@insite/mobius/Checkbox/Checkbox";
import { COLORS } from "@store/theme/design";
import { css } from "styled-components";

/* Store Custom Code - End */

const enum fields {
    expandByDefault = "expandByDefault",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.expandByDefault]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const {
        pages: {
            productList: { isLoading, productFilters, parameter, isSearchPage, filterQuery, productInfosByProductId },
        },
    } = state;
    const productsDataView = getProductsDataView(state, parameter);

    return {
        categoryFacets: getProductListDataViewProperty(state, "categoryFacets"),
        isCategoryPage: state.pages.productList.productFilters.pageCategoryId !== undefined,
        productsDataView,
    };
};

const mapDispatchToProps = {
    addProductFilters,
    removeProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export const productListCategoryFiltersStyles: ProductListFilterAccordionSectionStyles = {};

/* Store Custom Code - Start */
export const productListExplodedViewsFiltersStyles: ProductListFilterAccordionSectionStyles = {
    accordionSection: {
        headerProps: {
            css: css`
                ${productListFilterAccordionSectionStyles?.accordionSection?.headerProps?.css}
                padding: 0 1em 0;
                background: ${COLORS.secondary.base};
                button {
                    background-color: transparent;
                    border-top: 0;
                    border-bottom: 1px solid ${COLORS.border.dark};
                    color: ${COLORS.primary.base};
                    &[aria-expanded="false"] {
                        border-bottom: 1px solid transparent;
                    }
                }
            `,
        },
        panelProps: {
            css: css`
                ${productListFilterAccordionSectionStyles?.accordionSection?.panelProps?.css}
                padding: 1em;
                background: ${COLORS.secondary.base};
            `,
        },
    },
    checkBoxGroup: {
        css: css`
            ${productListFilterAccordionSectionStyles?.checkBoxGroup?.css}
            color:${COLORS.white};
        `,
    },
    checkBox: {
        css: css<CheckboxStyleProps>`
            ${productListFilterAccordionSectionStyles?.checkBox?.css}
            span[role="checkbox"] {
                border: 2px solid ${COLORS.white};
            }
            label {
                margin-top: 2px;
            }
        `,
    },
    checkBoxSelected: {
        css: css<CheckboxStyleProps>`
            ${productListFilterAccordionSectionStyles?.checkBox?.css}
            span[role="checkbox"] {
                &[aria-checked="true"]:not([aria-disabled="true"]),
                &[aria-checked="mixed"]:not([aria-disabled="true"]) {
                    border: 2px solid ${COLORS.white} !important;
                    color: ${COLORS.white};
                }
            }
            label {
                margin-top: 2px;
            }
        `,
    },
};
/* Store Custom Code - End */

const ProductListCategoryFilters: FC<Props> = ({
    categoryFacets,
    isCategoryPage,
    addProductFilters,
    removeProductFilters,
    fields,
    // Store Custom Code
    productsDataView,
}) => {
    // TODO ISC-11787 - make showMoreLimit configurable
    const showMoreLimit = 10;

    if (isCategoryPage || !categoryFacets?.length || !productsDataView || productsDataView.isLoading) {
        // Store Custom Code
        return null;
    }

    const facets = categoryFacets?.map(o => ({
        ...o,
        id: o.categoryId,
        name: o.shortDescription,
    }));

    const onChangeFacet = (facet: FacetModel) => {
        if (facet.selected) {
            removeProductFilters({ categoryId: facet.id });
        } else {
            addProductFilters({ categoryId: facet.id });
        }
    };

    /* Store Custom Code - Start */
    const explodedIds = React.useMemo(
        () =>
            (
                (productsDataView && !productsDataView.isLoading
                    ? (productsDataView as any)?.properties?.isExplodedViewSubCategory
                    : "") || ""
            ).split(","),
        [productsDataView],
    );
    const evFacets = React.useMemo(() => facets.filter(facet => explodedIds.includes(facet.id)), [facets, explodedIds]);
    const regularFacets = React.useMemo(
        () => facets.filter(facet => !explodedIds.includes(facet.id)),
        [facets, explodedIds],
    );

    if (!evFacets.length && !regularFacets.length) {
        return null;
    }

    return (
        <>
            {evFacets && evFacets.length > 0 && (
                <ProductListFiltersAccordionSection
                    title={translate("Exploded Views")}
                    facets={evFacets}
                    onChangeFacet={onChangeFacet}
                    showMoreLimit={showMoreLimit}
                    expandByDefault={true}
                    extendedStyles={productListExplodedViewsFiltersStyles}
                />
            )}
            {regularFacets && regularFacets.length > 0 && (
                <ProductListFiltersAccordionSection
                    title={translate("Categories")}
                    facets={regularFacets}
                    onChangeFacet={onChangeFacet}
                    showMoreLimit={showMoreLimit}
                    expandByDefault={fields.expandByDefault}
                    extendedStyles={productListCategoryFiltersStyles}
                />
            )}
        </>
    );
    /* Store Custom Code - End */
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListCategoryFilters),
    definition: {
        group: "Product List",
        displayName: "Category Filters",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.expandByDefault,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
